import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { useNavigate } from 'react-router-dom';
import {
    Alert, Avatar, Box, Card, CardContent, CardHeader,
    Chip, Divider, Link, Stack, Tooltip, Typography
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RouterIcon from '@mui/icons-material/Router';
import { navigateTabsWithQueries } from '../../types/cutterState';
import CutterMetricsStack from '../../common/CutterMetricsStack';

export interface CutterNodeData {
    serialNumber: string;
    hostname?: string;
    services?: string;
    machineType?: string;
    machineProduct?: string;
    machineMarket?: string;
    measureDate?: string;
    commercialVersion?: string;
    softwareUpdateIndex?: number;
    connectionRate?: number;
    hasWarning?: boolean;
    warnings?: string[];
}

// Mirrors getMarketAvatar from cutterState.ts but operates on raw string
const getMarketAvatarFromString = (market?: string): { label: string; color: string } => {
    switch (market) {
        case 'Auto':       return { label: 'Au',  color: '#be1622' };
        case 'Composite':  return { label: 'Co',  color: '#607d8b' };
        case 'Denim':      return { label: 'Den', color: '#6F8FAF' };
        case 'Fashion':    return { label: 'Fa',  color: '#460E0F' };
        case 'Footwear':   return { label: 'Fw',  color: '#8A7E72' };
        case 'Furniture':  return { label: 'Fu',  color: '#5C4D4E' };
        case 'Lingerie':   return { label: 'Lin', color: '#873379' };
        case 'TechTex':    return { label: 'Tex', color: '#0E776B' };
        default:           return { label: '?',   color: '#9e9e9e' };
    }
};

const WarningTooltipContent: React.FC<{ warnings: string[] }> = ({ warnings }) => (
    <Alert severity="warning" sx={{ minWidth: 400 }} >
        <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
            {warnings.map((w, i) => <li key={i}>{w}</li>)}
        </Box>
    </Alert>
);

const CutterNodeWithWarningInner: React.FC<NodeProps<CutterNodeData>> = ({ data }) => {
    const navigate = useNavigate();
    const marketAvatar = getMarketAvatarFromString(data.machineMarket);
    const connRate  = data.connectionRate ?? undefined;
    const swIndex   = data.softwareUpdateIndex ?? undefined;
    const hasStats  = connRate != null || swIndex != null;

    return (
        <>
            {/* ReactFlow connectors only — all visuals handled by MUI Card below */}
            <Handle type="target" position={Position.Bottom} />
            <Handle type="source" position={Position.Top} />

            <Card elevation={2} sx={{ minWidth: 165, maxWidth: 220, borderRadius: '6px', overflow: 'visible' }}>
                <CardHeader
                    sx={{ pb: 0.5, pt: 1, px: 1.5 }}
                    avatar={
                        <Tooltip title={data.machineMarket ?? ''} placement="left" arrow>
                            <Avatar sx={{ width: 28, height: 28, fontSize: '0.65rem', fontWeight: 700,
                                bgcolor: marketAvatar.color, color: '#fff' }}>
                                {marketAvatar.label}
                            </Avatar>
                        </Tooltip>
                    }
                    action={
                        data.hasWarning && data.warnings && data.warnings.length > 0 ? (
                            <Tooltip placement="top" arrow
                                title={<WarningTooltipContent warnings={data.warnings} />}
                                slotProps={{ tooltip: { sx: { padding: 0, backgroundColor: 'transparent', boxShadow: 'none' } } }}>
                                <WarningAmberIcon sx={{ fontSize: 18, color: 'error.main', cursor: 'help', mt: 0.5, mr: 0.5 }} />
                            </Tooltip>
                        ) : null
                    }
                    title={
                        <Link
                            href="#"
                            underline="hover"
                            sx={{ fontWeight: 700, fontSize: '0.75rem', lineHeight: 1.2, color: 'inherit' }}
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                e.preventDefault();
                                navigateTabsWithQueries(navigate, 'bookmarks', data.serialNumber);
                            }}
                        >
                            {data.serialNumber}
                        </Link>
                    }
                    subheader={
                        data.hostname ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.2 }}>
                                <RouterIcon sx={{ fontSize: 10, opacity: 0.55 }} />
                                <Typography sx={{ fontSize: '0.65rem', opacity: 0.75 }}>{data.hostname}</Typography>
                            </Box>
                        ) : null
                    }
                />

                <CardContent sx={{ pt: 0.5, pb: '8px !important', px: 1.5 }}>
                    {/* Machine type chip — product is shown by the parent group label */}
                    {data.machineType && (
                        <Box sx={{ mb: 0.5 }}>
                            <Chip label={data.machineType} size="small" variant="outlined"
                                sx={{ fontSize: '0.6rem', height: 16, fontWeight: 600, '& .MuiChip-label': { px: 0.6 } }} />
                        </Box>
                    )}

                    {/* Local services */}
                    {data.services && (
                        <Stack direction="row" spacing={0.4} flexWrap="wrap" useFlexGap sx={{ mb: hasStats ? 0.5 : 0 }}>
                            {data.services.split('+').filter(Boolean).map((svc: string, i: number) => (
                                <Chip key={i} label={svc} size="small"
                                    sx={{ fontSize: '0.6rem', height: 16, bgcolor: '#ede9fe', color: '#6b21a8', '& .MuiChip-label': { px: 0.6 } }} />
                            ))}
                        </Stack>
                    )}

                    {/* Stats bars */}
                    {hasStats && (
                        <CutterMetricsStack
                            commercialVersion={data.commercialVersion ?? null}
                            SoftwareUpdateIndex={data.softwareUpdateIndex ?? null}
                            ConnectionRate={data.connectionRate ?? null}
                        />
                    )}
                </CardContent>
            </Card>
        </>
    );
};

export const CutterNodeWithWarning = memo(CutterNodeWithWarningInner);
export default CutterNodeWithWarning;
